import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@/components/ui/Modal';
import { prepareTemplateUploads } from './uploadFlow';

interface UploadTemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

// 250 MB — Canva SVGs with many photo placeholders + decoration assets routinely
// land in the 50–200 MB range because every embedded image is base64-encoded
// inline. Must stay <= the layout-templates bucket file_size_limit in Supabase.
const MAX_SVG_BYTES = 250 * 1024 * 1024;

// In-flight upload state (svg + candidates + predictions) is persisted by
// prepareTemplateUploads via the IndexedDB wrapper in @/lib/idbTemplateUpload.
// SlotReviewPane reads it back on mount at /admin/layouts/review. IndexedDB
// (not sessionStorage) because real Canva SVGs embed base64 PNGs and can
// exceed the ~5 MB sessionStorage quota.
//
// Batch upload: N SVGs are detected + classified in parallel; the resulting
// queue is stashed in IDB and SlotReviewPane walks it one template at a time.

export function UploadTemplateDialog({ isOpen, onClose }: UploadTemplateDialogProps) {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isDragOver, setIsDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  // Status text shown during the parallel detect + classify window. `null`
  // outside of the busy window.
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setIsDragOver(false);
    setBusy(false);
    setStatus(null);
    setError(null);
  }

  function close() {
    reset();
    onClose();
  }

  async function handleSvgFiles(files: File[]): Promise<void> {
    // Per-file validation: keep valid ones, collect errors for the rest. We
    // don't abort the whole batch on one bad file — same policy as the
    // detect/classify failures inside prepareTemplateUploads.
    const validFiles: File[] = [];
    const validationErrors: string[] = [];
    for (const file of files) {
      if (file.type !== 'image/svg+xml' && !file.name.toLowerCase().endsWith('.svg')) {
        validationErrors.push(`${file.name} : format invalide (SVG attendu).`);
        continue;
      }
      if (file.size > MAX_SVG_BYTES) {
        validationErrors.push(`${file.name} : fichier trop volumineux (max 100 Mo).`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      setError(validationErrors.join(' ') || 'Aucun fichier SVG valide.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      // Parallel processing — we can't show incremental per-file progress
      // cheaply, so the status text is just a total count.
      setStatus(`Détection + classification IA pour ${validFiles.length} fichier(s)…`);
      const { ok, failed } = await prepareTemplateUploads(validFiles);

      // Surface validation + classification failures together; only those
      // that didn't succeed show up here. If everything failed, stay on the
      // dialog so the admin can see the banner.
      const allErrors = [
        ...validationErrors,
        ...failed.map((f) => `${f.fileName} : ${f.error}`),
      ];
      if (ok.length === 0) {
        setError(allErrors.join(' ') || 'Aucun fichier traité.');
        return;
      }
      if (allErrors.length > 0) {
        // Some succeeded, some failed — stash a banner the review pane can
        // pick up. For now we surface it here pre-navigation; the review
        // pane will mount on the successful queue regardless.
        // Simplest UX: alert the admin via the error banner just before
        // navigating away so they read it before the next screen.
        setError(allErrors.join(' '));
        // Give the user a beat to read, then navigate. Synchronous nav is
        // fine too — the React Router transition won't unmount us mid-paint.
      }
      onClose();
      navigate('/admin/layouts/review');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      setStatus(null);
    }
  }

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    // SNAPSHOT the FileList BEFORE resetting input value. e.target.files is
    // a *live* FileList — assigning e.target.value = '' clears it in place,
    // so any later read of `files.length` would see 0 and silently bail.
    const filesArray = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    if (filesArray.length === 0) return;
    await handleSvgFiles(filesArray);
  }

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={close} title="Importer un template SVG">
      <div className="p-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg mb-4" role="alert">
            {error}
          </div>
        )}

        {busy ? (
          <div className="aspect-[210/297] max-w-md mx-auto border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center bg-white">
            <svg className="w-8 h-8 animate-spin text-brand-500 mb-3" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-20" />
              <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            </svg>
            <p className="font-medium text-gray-700" role="status">
              {status ?? 'Traitement…'}
            </p>
          </div>
        ) : (
          <div className="max-w-md mx-auto">
            <div
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
                if (!isDragOver) setIsDragOver(true);
              }}
              onDragEnter={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={(e) => {
                if (e.currentTarget === e.target) setIsDragOver(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragOver(false);
                const files = e.dataTransfer.files;
                if (!files || files.length === 0) return;
                void handleSvgFiles(Array.from(files));
              }}
              className={`aspect-[210/297] w-full border-2 border-dashed rounded-xl flex flex-col items-center justify-center transition-colors cursor-pointer outline-none ${
                isDragOver
                  ? 'border-brand-500 bg-brand-500/5'
                  : 'border-gray-300 hover:border-brand-500 hover:bg-white'
              }`}
            >
              <div className="text-4xl mb-2">🎨</div>
              <p className="font-medium text-gray-700">
                {isDragOver ? 'Relâchez pour téléverser' : 'Téléverser un ou plusieurs SVG Canva'}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {isDragOver
                  ? 'Les fichiers seront analysés en parallèle'
                  : 'Cliquez ou glissez-déposez · File → Download → SVG, max 100 Mo par fichier'}
              </p>
            </div>
            <p className="text-xs text-gray-400 mt-4 leading-relaxed">
              Les zones photo sont détectées automatiquement, puis classifiées par IA. La revue
              (nom, catégorie, slots) se passe à l'écran suivant — un template à la fois quand
              vous en importez plusieurs.
            </p>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/svg+xml,.svg"
          multiple
          onChange={onPick}
          disabled={busy}
          className="hidden"
        />
      </div>
    </Modal>
  );
}
